import { useCallback, useEffect, useRef, useState } from 'react';
import { streamSse } from '@app/utils/sse';
import { resolveErrorMessage } from '@app/utils/errors';
import { type Artifact, type Instance, type RunbookClient, type Step, isTerminal } from './api';
export function useExecution(client: RunbookClient, id: string) {
  const [instance, setInstance] = useState<Instance>();
  const [steps, setSteps] = useState<Step[]>([]), [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [report, setReport] = useState(''), [error, setError] = useState(''), [outputError, setOutputError] = useState('');
  const [loading, setLoading] = useState(true), [connection, setConnection] = useState('Connecting');
  const [updatedAt, setUpdatedAt] = useState<string>();
  const [activity, setActivity] = useState<Array<{ key: string; message: string; time: string }>>([]);
  const [refresh, setRefresh] = useState(0);
  const requestRefresh = useCallback(() => setRefresh(n => n + 1), []);
  const refreshRef = useRef<() => void>(() => {});
  useEffect(() => {
    setInstance(undefined); setSteps([]); setArtifacts([]); setReport(''); setError(''); setOutputError(''); setActivity([]); setLoading(true);
  }, [client, id]);
  useEffect(() => {
    let active = true, fetching = false, pending = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let eventTimer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      if (!active) return;
      if (fetching) { pending = true; return; }
      fetching = true;
      clearTimeout(timer);
      let terminal = false;
      try {
        const next = await client.instance(id);
        if (!active) return;
        terminal = isTerminal(next.status);
        setInstance(next); setError('');
        const outputs = await Promise.allSettled([client.steps(id), client.artifacts(id), next.execution_id ? client.report(id) : Promise.resolve('')]);
        if (!active) return;
        const [stepResult, artifactResult, reportResult] = outputs;
        if (stepResult.status === 'fulfilled') setSteps(stepResult.value.items ?? []);
        if (artifactResult.status === 'fulfilled') setArtifacts(artifactResult.value.items ?? []);
        if (reportResult.status === 'fulfilled') setReport(reportResult.value);
        const failed = outputs.map((v, i) => v.status === 'rejected' ? ['steps', 'artifacts', 'report'][i] : '').filter(Boolean);
        setOutputError(failed.length ? `Could not refresh ${failed.join(', ')}. Previously loaded results are still shown.` : '');
        setUpdatedAt(new Date().toISOString());
      } catch (caught) { const message = await resolveErrorMessage(caught, 'Could not load this execution.'); if (active) setError(message); }
      finally {
        fetching = false;
        if (active) {
          setLoading(false);
          if (pending) { pending = false; timer = setTimeout(load, 300); }
          else if (!terminal) timer = setTimeout(load, 5000);
        }
      }
    };
    refreshRef.current = () => {
      if (eventTimer) return;
      eventTimer = setTimeout(() => { eventTimer = undefined; void load(); }, 250);
    };
    void load();
    return () => { active = false; clearTimeout(timer); clearTimeout(eventTimer); };
  }, [client, id, refresh]);
  const streamId = instance?.instance_id;
  const terminal = isTerminal(instance?.status);
  useEffect(() => {
    if (!streamId || terminal) { setConnection(terminal ? 'Saved execution' : 'Waiting for runtime'); return; }
    const controller = new AbortController();
    const seen = new Set<string>();
    setConnection('Connecting');
    const url = client.eventsUrl(streamId);
    void streamSse(url, {
      // Each connection receives the latest persisted snapshot, including after reconnect.
      getUrl: () => url,
      sendLastEventId: false,
      reconnect: { enabled: true, maxAttempts: 3, retryDelayMs: 1500 },
      onOpen: () => { if (!controller.signal.aborted) { setConnection('Live updates'); refreshRef.current(); } },
      onEvent: (type, raw, eventId) => {
        if (controller.signal.aborted) return;
        if (type === 'error') { setConnection('Reconnecting · polling enabled'); refreshRef.current(); return; }
        let payload: Record<string, unknown>;
        try { payload = JSON.parse(raw); } catch { return; }
        const sequence = eventId || (typeof payload.eventId === 'number' ? String(payload.eventId) : undefined);
        if (sequence) { if (seen.has(sequence)) return; seen.add(sequence); }
        refreshRef.current();
        const message = typeof payload.message === 'string' ? payload.message : typeof payload.detail === 'string' ? payload.detail : '';
        if (message && ['progress', 'status', 'plan_step', 'plan_step_update'].includes(type)) {
          setActivity(items => [...items, { key: sequence || `${Date.now()}-${items.length}`, message, time: new Date().toISOString() }].slice(-100));
        }
      }
    }, controller.signal).then(() => { if (!controller.signal.aborted) setConnection('Polling updates'); }).catch(() => { if (!controller.signal.aborted) setConnection('Stream unavailable · polling updates'); });
    return () => controller.abort();
  }, [client, streamId, terminal]);
  return { instance, steps, artifacts, report, error, outputError, loading, connection, updatedAt, activity, retry: requestRefresh };
}
